/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * The wire contract between the server publisher and the client subscriber.
 *
 * Chat uses plain Ably Pub/Sub with one channel per room, plus one inbox channel per
 * profile. The inbox exists so a direct or group message can reach someone who is
 * attached to a different room, without subscribing to every room they belong to.
 * Payloads are deliberately slim: they carry the minimum needed to update the Apollo
 * cache in place, and a message that would exceed CHAT_PAYLOAD_MAX_BYTES is published
 * truncated with `truncated: true` so the client refetches that one message instead
 * of the server paying for a large publish.
 */

export const CHAT_CHANNEL_PREFIX = 'chat:room:'

/** The single Ably channel for a room. Presence and typing ride on this channel too. */
export const chatRoomChannelName = (chatRoomId: string): string => `${CHAT_CHANNEL_PREFIX}${chatRoomId}`

/** Personal channel that receives direct and group messages for rooms the member is not watching. */
export const CHAT_INBOX_CHANNEL_PREFIX = 'chat:inbox:'

export const chatInboxChannelName = (profileId: string): string => `${CHAT_INBOX_CHANNEL_PREFIX}${profileId}`

/** Extract a room id from a channel name, or null if the name is not a chat channel. */
export const chatRoomIdFromChannelName = (channelName: string): string | null =>
  channelName.startsWith(CHAT_CHANNEL_PREFIX) ? channelName.slice(CHAT_CHANNEL_PREFIX.length) : null

export const ChatRealtimeEvent = {
  MESSAGE_CREATED: 'message.created',
  MESSAGE_UPDATED: 'message.updated',
  MESSAGE_DELETED: 'message.deleted',
  /** Coalesced reaction state for one message, debounced server side. */
  REACTIONS_CHANGED: 'reactions.changed',
  MEMBER_JOINED: 'member.joined',
  MEMBER_LEFT: 'member.left',
  ROOM_UPDATED: 'room.updated',
  /** A member's read cursor advanced; powers live seen-by indicators. */
  READ_CHANGED: 'read.changed',
  /**
   * A direct or group message for a room the member is not necessarily watching.
   * Published only on that member's inbox channel.
   */
  INBOX_MESSAGE: 'inbox.message',
  /** The only event clients are allowed to publish. */
  TYPING: 'typing',
} as const

export type ChatRealtimeEvent = (typeof ChatRealtimeEvent)[keyof typeof ChatRealtimeEvent]

/**
 * Events the server publishes. Server publishes carry no Ably `clientId`, so the
 * subscriber rejects any of these that arrive with one -- that is a client
 * impersonating the server. See `isSpoofedChatEvent`.
 */
const SERVER_ONLY_EVENTS = new Set<string>([
  ChatRealtimeEvent.MESSAGE_CREATED,
  ChatRealtimeEvent.MESSAGE_UPDATED,
  ChatRealtimeEvent.MESSAGE_DELETED,
  ChatRealtimeEvent.REACTIONS_CHANGED,
  ChatRealtimeEvent.MEMBER_JOINED,
  ChatRealtimeEvent.MEMBER_LEFT,
  ChatRealtimeEvent.ROOM_UPDATED,
  ChatRealtimeEvent.READ_CHANGED,
  ChatRealtimeEvent.INBOX_MESSAGE,
])

/**
 * Clients hold `publish` capability on their room channels so they can send typing
 * indicators without a server round trip. That means a malicious client could also
 * publish a fabricated `message.created`. Ably stamps every client publish with the
 * token's `clientId` and server publishes made with an API key have none, so a
 * server-only event that arrives with a clientId did not come from us.
 */
export const isSpoofedChatEvent = (eventName: string, clientId: string | null | undefined): boolean =>
  SERVER_ONLY_EVENTS.has(eventName) && clientId != null && clientId !== ''

export interface ChatRealtimeSender {
  id: string
  name: string
  /**
   * Pre-resolved small avatar URL. Sending the resolved URL rather than a storage path
   * keeps the client from having to sign or build one for a profile it may not have
   * cached yet.
   */
  avatarUrl: string | null
}

/**
 * Presence enter payload and the client-side online-users list.
 *
 * `id` is always taken from the Ably `clientId`, never from this object -- presence
 * data is client-authored and untrusted. The rest is display-only so the list can
 * render without a GraphQL round trip on every join or leave.
 */
export interface ChatPresenceMember {
  id: string
  name: string
  avatarUrl: string | null
  /** Needed so SFProfileBanner does not treat a missing rating as NSFW and blur the avatar. */
  nsfwRating: string | null
  location: string | null
  locationLat: number | null
  locationLong: number | null
  isVerified: boolean
  isSupporter: boolean
}

/** Fields written to `presence.enter`. Identity is the token `clientId`. */
export type ChatPresenceEnterData = Omit<ChatPresenceMember, 'id'>

export interface ChatMessageCreatedPayload {
  id: string
  chatRoomId: string
  /** Echoed back so the sender can reconcile its optimistic message exactly. */
  clientMessageId: string | null
  createdAt: string
  sender: ChatRealtimeSender | null
  /** Preview text for the sidebar. Always present, even when truncated. */
  preview: string
  /** ProseMirror document, omitted when the payload had to be truncated. */
  content?: unknown
  /** Set when content was dropped for size. The client refetches this message by id. */
  truncated?: boolean
  hasMedia: boolean
  replyToId: string | null
}

export interface ChatMessageUpdatedPayload {
  id: string
  chatRoomId: string
  preview: string
  content?: unknown
  truncated?: boolean
  editedAt: string
}

export interface ChatMessageDeletedPayload {
  id: string
  chatRoomId: string
}

export interface ChatReactionSummary {
  emoji: string
  count: number
  /** Profile ids of reactors, capped; used to render "you reacted" state. */
  profileIds: string[]
}

export interface ChatReactionsChangedPayload {
  messageId: string
  chatRoomId: string
  reactions: ChatReactionSummary[]
}

export interface ChatMemberChangedPayload {
  chatRoomId: string
  profileId: string
  memberCount: number
}

export interface ChatRoomUpdatedPayload {
  chatRoomId: string
  name: string | null
  description: string | null
  isPublic: boolean
  archivedAt: string | null
  deletedAt: string | null
}

/**
 * A member advanced their read cursor. Clients patch `seenBy` / `seenByCount` on
 * cached messages with `createdAt <= lastReadAt` rather than refetching the timeline.
 */
export interface ChatReadChangedPayload {
  chatRoomId: string
  profileId: string
  lastReadAt: string
  lastReadMessageId: string | null
  reader: ChatRealtimeSender
}

/** Direct and group only. Channels stay on the room channel and do not use the inbox. */
export type ChatInboxRoomType = 'DIRECT' | 'GROUP'

/**
 * Slim notice that a direct or group message arrived in a room the recipient may not
 * have open. No message body: the preview is enough for a banner, and the room list
 * refetch supplies the unread count.
 */
export interface ChatInboxMessagePayload {
  messageId: string
  chatRoomId: string
  roomType: ChatInboxRoomType
  /** Group name. Null for a direct chat, which is identified by the sender. */
  roomName: string | null
  sender: ChatRealtimeSender | null
  preview: string
  createdAt: string
}

/** The only client-published event. Deliberately has no matching "stopped typing". */
export interface ChatTypingPayload {
  chatRoomId: string
  profileId: string
  profileName: string
}

export type ChatRealtimePayloadMap = {
  [ChatRealtimeEvent.MESSAGE_CREATED]: ChatMessageCreatedPayload
  [ChatRealtimeEvent.MESSAGE_UPDATED]: ChatMessageUpdatedPayload
  [ChatRealtimeEvent.MESSAGE_DELETED]: ChatMessageDeletedPayload
  [ChatRealtimeEvent.REACTIONS_CHANGED]: ChatReactionsChangedPayload
  [ChatRealtimeEvent.MEMBER_JOINED]: ChatMemberChangedPayload
  [ChatRealtimeEvent.MEMBER_LEFT]: ChatMemberChangedPayload
  [ChatRealtimeEvent.ROOM_UPDATED]: ChatRoomUpdatedPayload
  [ChatRealtimeEvent.READ_CHANGED]: ChatReadChangedPayload
  [ChatRealtimeEvent.INBOX_MESSAGE]: ChatInboxMessagePayload
  [ChatRealtimeEvent.TYPING]: ChatTypingPayload
}

/**
 * How long a typing indicator stays on screen. There is no "stopped typing" event --
 * publishing one would double typing traffic -- so the indicator expires locally.
 */
export const CHAT_TYPING_TTL_MS = 10_000
